import Tooltip from '@mui/material/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { countryInfosQuery } from 'api/queries';
import CountryFlag from 'library/components/controls/display/CountryFlag';
import {
	getShortLocationFormat,
	isUSTerritory,
} from 'library/formatters/LocationFormatter';
import { useContainerWidth } from 'library/hooks/useContainerWidth';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';

const COUNTRY_NAME_FORMAT_LONG = 'long';
const MIN_WIDTH_FOR_TEXT = 100;

export interface LocationCountryTextProps {
	city?: string | null;
	countryCode?: string | null;
	countryNameFormat?: 'short' | 'long';
	showCountryFlag?: boolean;
	showCountryCode?: boolean;
	isDynamicWidth?: boolean;
}

export const LocationCountryText = ({
	city,
	countryCode,
	countryNameFormat = COUNTRY_NAME_FORMAT_LONG,
	showCountryFlag = true,
	showCountryCode = false,
	isDynamicWidth = false,
}: LocationCountryTextProps): React.JSX.Element => {
	const { ref, width } = useContainerWidth();
	const { t } = useTranslation('common');
	const { api } = useProductCenterApi();

	const languageCode = useAppSelector(selectUser).language;
	const { data } = useQuery({
		...countryInfosQuery(api, languageCode),
		enabled: countryNameFormat === COUNTRY_NAME_FORMAT_LONG,
	});

	const countryName = useMemo(
		() =>
			data?.find(({ alpha2 }) => alpha2 === countryCode)
				?.translatedCountryName || countryCode,
		[data, countryCode]
	);

	const isUSTerritoryCountryCode = useMemo(
		() => isUSTerritory(countryCode),
		[countryCode]
	);
	const countryText = useMemo(() => {
		const location =
			city != null
				? getShortLocationFormat(countryName, city)
				: countryName;
		const territory = isUSTerritoryCountryCode
			? ` (${t('us-territory')})`
			: '';
		return `${location} ${territory}`.trim();
	}, [city, countryName, isUSTerritoryCountryCode, t]);

	const hasSpaceForText = isDynamicWidth ? width > MIN_WIDTH_FOR_TEXT : true;

	return (
		<span
			ref={ref}
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				overflow: 'hidden',
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
			}}>
			{showCountryFlag && countryCode && (
				<Tooltip
					title={!hasSpaceForText ? countryText : ''}
					disableHoverListener={hasSpaceForText}>
					<span style={{ display: 'inline-flex', flexShrink: 0 }}>
						<CountryFlag countryCode={countryCode} />
					</span>
				</Tooltip>
			)}

			{countryName && (hasSpaceForText || !showCountryFlag) && (
				<span style={{ minWidth: 0 }}>
					{showCountryCode && countryCode && ` (${countryCode}) `}
					{countryText}
				</span>
			)}
		</span>
	);
};
